import React from "react";
import styled, { keyframes } from "styled-components";

/**
 * 汎用プログレスバー（グローバルコンポーネント）。
 *
 * - `value` を渡すと確定（determinate）バー（0–100%）。
 * - `value` 省略時は不定（indeterminate）アニメーション。進捗率が取得できない
 *   処理（PDF 生成など）のローディング表示に使う。
 */
interface ProgressBarProps {
  /** 0–100 の進捗率。未指定なら不定（indeterminate）アニメーション。 */
  value?: number;
  /** バーの高さ（px）。既定 4。 */
  height?: number;
  /** トラックの幅（CSS 値）。既定 "100%"。 */
  width?: string;
  className?: string;
}

const indeterminateSlide = keyframes`
  0% { left: -40%; width: 40%; }
  50% { left: 30%; width: 50%; }
  100% { left: 100%; width: 40%; }
`;

const Track = styled.div<{ $height: number; $width: string }>`
  position: relative;
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height}px;
  background: var(--soremo-border);
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
`;

const DeterminateFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => Math.max(0, Math.min(100, $value))}%;
  background: var(--soremo-blue);
  border-radius: 999px;
  transition: width 0.2s ease;
`;

const IndeterminateFill = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  background: var(--soremo-blue);
  border-radius: 999px;
  animation: ${indeterminateSlide} 1.2s ease-in-out infinite;
`;

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  height = 4,
  width = "100%",
  className,
}) => {
  const isDeterminate = typeof value === "number";
  return (
    <Track
      $height={height}
      $width={width}
      className={className}
      role="progressbar"
      aria-valuenow={isDeterminate ? value : undefined}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {isDeterminate ? (
        <DeterminateFill $value={value as number} />
      ) : (
        <IndeterminateFill />
      )}
    </Track>
  );
};

export default ProgressBar;
